import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import cn from 'classnames';
import { UnstyledButton } from '../button';
import { MenuList } from '../list';
import { focusFirstAvailable } from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type Button from '../button';
import type { ComponentChildren, ComponentProps, Ref, TargetedEvent, TargetedToggleEvent } from 'preact';
import type { HTMLAttributes } from '../../types/utility';
import type { Position } from '../../types/common';

interface BaseProps {
  children?: ComponentChildren;
  innerRef?: Ref<HTMLDetailsElement>;
  summaryProps?: HTMLAttributes<HTMLElement> & { 'data-icon'?: ButtonProps['icon'] };
  text: string;
}

type Props = BaseProps & Omit<HTMLAttributes<HTMLDetailsElement>, 'size'>;

type ButtonProps = Omit<ComponentProps<typeof Button>, 'children'> &
  Pick<HTMLAttributes<HTMLDetailsElement>, 'onToggle'>;

function repositionContent(e: TargetedEvent<HTMLDetailsElement>) {
  const { currentTarget } = e;

  if (currentTarget.dataset.enableReposition === 'false') {
    return;
  }

  const content = currentTarget.lastElementChild as HTMLElement;
  if (!currentTarget.open) {
    content.style.top = '';
    content.style.bottom = '';
    content.style.left = '';
    content.style.right = '';
    content.style.transform = '';
    return;
  }

  const { innerHeight, innerWidth } = window;
  const { bottom, left, right, top } = content.getBoundingClientRect();

  // check if top or bottom extends past viewport
  if (bottom > innerHeight) {
    content.style.top = 'auto';
    content.style.bottom = '100%';
    content.style.transform = 'none';
  } else if (top < 0) {
    content.style.top = '100%';
    content.style.bottom = 'auto';
    content.style.transform = 'none';
  }

  // check if left or right extends past viewport
  if (right > innerWidth) {
    content.style.left = 'auto';
    content.style.right = '100%';
    content.style.transform = 'none';
  } else if (left < 0) {
    content.style.left = '100%';
    content.style.right = 'auto';
    content.style.transform = 'none';
  }
}

export default function Details({
  children,
  innerRef,
  // React 19 delivers `ref` as a regular prop; keep it out of the spread so
  // it can't clobber `innerRef` (Popover's hover wiring depends on it)
  ref: forwardedRef,
  summaryProps = {},
  text,
  onToggle,
  ...props
}: Props) {
  const mergedDetailsRef = useMergedRef(innerRef, forwardedRef);
  const handleToggle = useCallback(
    (e: TargetedToggleEvent<HTMLDetailsElement>) => {
      const { currentTarget } = e;
      if (currentTarget.open) {
        repositionContent(e);
        focusFirstAvailable(currentTarget, { selector: ':not(details, summary)', isSync: false });
      }
      onToggle?.(e);
    },
    [onToggle],
  );

  return (
    /*
      A caller-supplied `ref` is destructured out of `props` above (so the
      spread can't clobber `innerRef`) and merged back in alongside it.
    */
    <details {...props} ref={mergedDetailsRef} onToggle={handleToggle}>
      <summary {...summaryProps}>{text}</summary>
      {children}
    </details>
  );
}

/*
  Attached via native addEventListener (not JSX props) because React doesn't
  recognize onFocusIn/onFocusOut (it normalizes focus/blur to bubble via
  onFocus/onBlur instead, but Preact's onFocus/onBlur don't bubble). Native
  listeners sidestep the synthetic-event layer entirely and work identically
  in both frameworks.

  mouseenter/mouseleave (not mouseover/mouseout) because they fire only on
  the outer element boundary and don't bubble — so moving the pointer
  between children inside the popover doesn't trigger a spurious close.
*/
function handleHover(e: Event) {
  const target = e.currentTarget as HTMLDetailsElement;
  if (e.type === 'mouseenter' || e.type === 'focusin') {
    target.open = true;
  } else if (
    e.type === 'mouseleave' ||
    (e.type === 'focusout' && !target.contains((e as FocusEvent).relatedTarget as HTMLElement))
  ) {
    target.open = false;
  }
}

export function Popover({
  children,
  className,
  disabled,
  enableReposition = false,
  icon,
  iconPosition,
  isIconOnly,
  isLoading,
  hierarchy = 'primary',
  mode = 'click',
  position = 'bottom-center',
  size = 'medium',
  title,
  text,
  onToggle,
  // Discard: Popover owns detailsRef for hover wiring. A caller-passed
  // innerRef would otherwise fall into ...props, spread after innerRef={detailsRef},
  // and silently clobber it — breaking hover mode entirely.
  innerRef: _innerRef,
  ...props
}: Props &
  ButtonProps & { mode?: 'click' | 'hover'; position?: Position; enableReposition?: boolean }) {
  const buttonClassName = cn('visual-button', {
    [hierarchy]: hierarchy !== 'primary',
    ['icon-after']: !!iconPosition && iconPosition !== 'before',
    [size]: size !== 'medium',
    ['icon-only']: isIconOnly,
    ['is-loading']: isLoading,
  });

  const detailsClassName = cn('popover', {
    [className]: !!className,
    ['popover-mode-hover']: mode === 'hover',
  });

  const popoverClassName = cn('popover-content', {
    [position]: position !== 'bottom-center',
  });

  const summaryProps = {
    className: buttonClassName,
    'aria-label': isIconOnly ? text : undefined,
    'data-icon': icon,
    'aria-haspopup': true,
    title,
    disabled: disabled,
    tabIndex: disabled ? -1 : undefined,
  };

  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    if (mode !== 'hover') {
      return;
    }
    const el = detailsRef.current;
    if (!el) {
      return;
    }

    el.addEventListener('mouseenter', handleHover);
    el.addEventListener('mouseleave', handleHover);
    el.addEventListener('focusin', handleHover);
    el.addEventListener('focusout', handleHover);

    return () => {
      el.removeEventListener('mouseenter', handleHover);
      el.removeEventListener('mouseleave', handleHover);
      el.removeEventListener('focusin', handleHover);
      el.removeEventListener('focusout', handleHover);
    };
  }, [mode]);

  return (
    <Details
      innerRef={detailsRef}
      className={detailsClassName}
      data-enable-reposition={enableReposition}
      summaryProps={summaryProps}
      text={!isIconOnly && text}
      onToggle={onToggle}
      {...(props as unknown as Props)}
    >
      <div className={popoverClassName}>{children}</div>
    </Details>
  );
}

function handleClosePopover(e) {
  const popover = e.currentTarget.closest('details');
  if (popover) {
    popover.open = false;

    if (e.code === 'Escape') {
      popover.firstElementChild?.focus();
    }
  }
}

interface MenuButtonItemBase {
  disabled?: boolean;
  label: ComponentChildren;
  value?: string;
  className?: string;
  onClick?: (e: TargetedEvent) => void;
  [key: `data-${string}`]: string;
}

/** Rendered as an <a>. `target="_top"` opts a same-origin link out of SPA routers (e.g. preact-iso). */
interface MenuButtonLinkItem extends MenuButtonItemBase {
  href: string;
  target?: '_self' | '_blank' | '_parent' | '_top';
}

/** Rendered as a <button>, which has no `target` attribute. */
interface MenuButtonActionItem extends MenuButtonItemBase {
  href?: undefined;
  target?: undefined;
}

export type MenuButtonItem = MenuButtonLinkItem | MenuButtonActionItem;

export function MenuButton({
  className,
  items,
  defaultValue,
  onChange,
  onToggle,
  ...props
}: ComponentProps<typeof Popover> & {
  items: MenuButtonItem[];
  defaultValue?: string;
  onChange?: (e: TargetedEvent) => void;
}) {
  const isSelectable = !!onChange;
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const itemRole = isSelectable ? 'menuitemradio' : 'menuitem';

  const classList = cn('menu-button', className);

  const handleChange = useCallback(
    (e: TargetedEvent<HTMLButtonElement>) => {
      setSelectedValue(e.currentTarget.value);
      onChange?.(e);
    },
    [onChange],
  );

  const menuItems = useMemo(() => {
    return (
      items?.map(({ label, href, onClick, ...buttonProps }) => {
        const isChecked = isSelectable && buttonProps.value === selectedValue;

        return href ? (
          <a
            key={label}
            href={href}
            role={itemRole}
            aria-checked={isSelectable ? isChecked : undefined}
            onClick={isSelectable ? handleChange : onClick}
            {...buttonProps}
            tabIndex={-1}
          >
            {label}
          </a>
        ) : (
          <UnstyledButton
            key={label}
            role={itemRole}
            aria-checked={isSelectable ? isChecked : undefined}
            onClick={isSelectable ? handleChange : onClick}
            {...buttonProps}
            tabIndex={-1}
          >
            {label}
          </UnstyledButton>
        );
      }) ?? []
    );
  }, [items, isSelectable, selectedValue, itemRole, handleChange]);

  const handleKeyDown = useCallback((e) => {
    const { currentTarget } = e;
    const { activeElement } = document;
    const items =
      Array.from(
        currentTarget.querySelectorAll(`[role="${itemRole}"]`) as NodeListOf<HTMLButtonElement>,
      ) ?? [];
    const activeIndex = items.findIndex((item) => item === activeElement);

    switch (e.code) {
      case 'ArrowDown': {
        e.preventDefault();

        let nextIndex = activeIndex + 1;
        let i = 0;

        do {
          if (nextIndex >= items.length) {
            nextIndex = 0;
          }

          const item = items[nextIndex];

          if (item?.disabled) {
            nextIndex++;
          } else {
            break;
          }
        } while (nextIndex <= items.length && ++i < items.length);

        items[nextIndex]?.focus();

        return;
      }
      case 'ArrowUp': {
        e.preventDefault();

        let prevIndex = activeIndex - 1;
        let i = 0;

        do {
          if (prevIndex < 0) {
            prevIndex = items.length - 1;
          }

          const item = items[prevIndex];

          if (item?.disabled) {
            prevIndex--;
          } else {
            break;
          }
        } while (prevIndex > -1 && ++i < items.length);

        items[prevIndex]?.focus();

        return;
      }
      case 'Escape': {
        handleClosePopover(e);
      }
    }
  }, [itemRole]);

  const handleToggle = useCallback(
    (e) => {
      if (isSelectable && e.currentTarget.open && selectedValue) {
        focusFirstAvailable(e.currentTarget, { selector: '[aria-checked="true"]', isSync: false });
      }
      onToggle?.(e);
    },
    [isSelectable, selectedValue, onToggle],
  );

  return (
    <Popover text="Menu" icon="chevron-down" iconPosition="after" {...props} className={classList} onToggle={handleToggle}>
      <MenuList
        orientation="vertical"
        items={menuItems}
        role="menu"
        onClick={handleClosePopover}
        onKeyDown={handleKeyDown}
      />
    </Popover>
  );
}
